// Publish / Issue wizard — ONE component, two entry points (Form Templates library
// "Issue / allocate", and a project folder "Issue into this folder"). Folder selection
// is a first-class MANDATORY step using the P3 FolderTree picker on the target project's
// actual tree. Same backend issue creation (POST /issues -> POST /issue) for both routes.
import { useEffect, useMemo, useState } from "react";
import {
    X, ChevronRight, ChevronLeft, FolderOpen, Loader2, Send, FileText, Bell,
    Upload, ClipboardList, Info, Users, Link2, Check, Copy, QrCode,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import FolderTree from "@/components/documents/FolderTree";

function publicLink(token) {
    return `${window.location.origin}/s/${token}`;
}

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,#C75A1B)]";

// issue "kind" presets -> (issue_type, completion_mode)
const KINDS = [
    { key: "info", label: "Information to acknowledge", icon: Info, type: "information", mode: "acknowledge", help: "Recipients confirm they've read it." },
    { key: "ack", label: "Action — acknowledge", icon: Bell, type: "action", mode: "acknowledge", help: "Tracked action, completed by acknowledgement." },
    { key: "upload", label: "Action — upload evidence", icon: Upload, type: "action", mode: "upload", help: "Recipient returns a completed file." },
    { key: "form", label: "Action — complete a form", icon: ClipboardList, type: "action", mode: "form", help: "Recipient completes a linked form." },
];

export default function PublishWizard({ source, preset, clients: clientsProp, user, onClose, onIssued }) {
    const fromTemplate = !!source?.id;
    const [clients, setClients] = useState(clientsProp || []);
    const [projects, setProjects] = useState([]);
    const [folders, setFolders] = useState([]);
    const [people, setPeople] = useState([]);
    const [formTemplates, setFormTemplates] = useState([]);
    const [busy, setBusy] = useState(false);

    const [clientId, setClientId] = useState(preset?.clientId || "");
    const [projectId, setProjectId] = useState(preset?.projectId || "");
    const [folderId, setFolderId] = useState(preset?.folderId || "");
    const [kind, setKind] = useState(fromTemplate ? "form" : "info");
    const [sourceTemplateId, setSourceTemplateId] = useState(source?.id || "");
    const [scope, setScope] = useState("any");
    const [mode, setMode] = useState("named");        // recipient_mode
    const [expectedCount, setExpectedCount] = useState(10);
    const [dueDate, setDueDate] = useState("");
    const [instructions, setInstructions] = useState("");
    const [portalVisible, setPortalVisible] = useState(true);
    const [title, setTitle] = useState(source?.name || "");
    const [picked, setPicked] = useState({});         // contact_id -> recipient
    const [freeName, setFreeName] = useState("");
    const [freeEmail, setFreeEmail] = useState("");
    const [freeRecipients, setFreeRecipients] = useState([]);

    const startStep = (preset?.folderId && preset?.projectId) ? "behaviour" : "target";
    const [step, setStep] = useState(startStep);
    const [issuedOpen, setIssuedOpen] = useState(null);   // open-mode issued issue → share panel

    const kindDef = KINDS.find((k) => k.key === kind);

    // data loads
    useEffect(() => { if (!clientsProp?.length && user) api.get("/clients").then((r) => setClients(r.data)).catch(() => { }); }, [clientsProp, user]);
    useEffect(() => {
        if (!clientId) { setProjects([]); return; }
        api.get("/projects", { params: { client_id: clientId } }).then((r) => setProjects(r.data)).catch(() => { });
    }, [clientId]);
    useEffect(() => {
        if (!projectId) { setFolders([]); setPeople([]); return; }
        api.get("/folders", { params: { client_id: clientId, project_id: projectId, scope: "project" } }).then((r) => setFolders(r.data)).catch(() => { });
        api.get(`/projects/${projectId}/access`).then((r) => setPeople(r.data.client_people || [])).catch(() => { });
    }, [projectId, clientId]);
    useEffect(() => { if (kind === "form" && !fromTemplate) api.get("/forms/templates", { params: { status: "published" } }).then((r) => setFormTemplates(r.data)).catch(() => { }); }, [kind, fromTemplate]);

    const folderName = useMemo(() => folders.find((f) => f.id === folderId)?.name || preset?.folderName, [folders, folderId, preset]);
    const projectLabel = useMemo(() => projects.find((p) => p.id === projectId)?.name || preset?.projectLabel, [projects, projectId, preset]);

    const recipients = useMemo(() => {
        const fromPicked = Object.values(picked);
        return [...fromPicked, ...freeRecipients];
    }, [picked, freeRecipients]);

    function togglePerson(p) {
        setPicked((cur) => {
            const next = { ...cur };
            if (next[p.contact_id]) delete next[p.contact_id];
            else next[p.contact_id] = { contact_id: p.contact_id, name: p.name, email: p.email };
            return next;
        });
    }
    function addFree() {
        if (!freeName.trim()) return;
        setFreeRecipients((r) => [...r, { name: freeName.trim(), email: freeEmail.trim() || null }]);
        setFreeName(""); setFreeEmail("");
    }

    function validateStep() {
        if (step === "target") {
            if (!clientId) return "Choose a client";
            if (!projectId) return "Choose a project";
            if (!folderId) return "Select a destination folder";
        }
        if (step === "behaviour") {
            if (!title.trim()) return "Give it a title";
            if (kind === "form" && !sourceTemplateId) return "Pick a form template";
        }
        if (step === "recipients") {
            if (mode === "named" && recipients.length === 0) return "Add at least one recipient";
            if (mode === "open" && (!expectedCount || expectedCount < 1)) return "Set an approximate headcount";
        }
        return null;
    }

    const STEPS = ["target", "behaviour", "recipients", "review"];
    function next() {
        const err = validateStep();
        if (err) { toast.error(err); return; }
        const idx = STEPS.indexOf(step);
        setStep(STEPS[Math.min(idx + 1, STEPS.length - 1)]);
    }
    function back() {
        const idx = STEPS.indexOf(step);
        // don't go back into target if it was preset
        const floor = (preset?.folderId && preset?.projectId) ? 1 : 0;
        setStep(STEPS[Math.max(idx - 1, floor)]);
    }

    async function submit(issueNow) {
        const err = validateStep();
        if (err) { toast.error(err); return; }
        setBusy(true);
        try {
            const body = {
                source_type: kind === "form" ? "form_template" : "blank",
                source_id: kind === "form" ? sourceTemplateId : null,
                title: title.trim(),
                issue_type: kindDef.type, completion_mode: kindDef.mode,
                completion_scope: scope, recipient_mode: mode,
                expected_count: mode === "open" ? Number(expectedCount) : null,
                due_date: dueDate || null, instructions: instructions || null,
                portal_visible: portalVisible,
                recipients: mode === "named" ? recipients : [],
                targets: [{ project_id: projectId, folder_id: folderId }],
            };
            const { data } = await api.post("/issues", body);
            const created = data[0];
            if (issueNow) {
                const { data: issued } = await api.post(`/issues/${created.id}/issue`);
                onIssued?.(issued);
                // Open-mode → keep the wizard open on a share panel (link + QR); otherwise close.
                if (mode === "open" && issued?.submission_token) {
                    toast.success("Issued — share the submission link");
                    setIssuedOpen(issued);
                    setBusy(false);
                    return;
                }
                toast.success("Issued to recipients");
            } else {
                toast.success("Saved as draft (Not issued)");
                onIssued?.(created);
            }
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not issue");
        }
        setBusy(false);
    }

    const isInternal = user && ["director", "administrator", "consultant"].includes(user.role);

    return (
        <div className="fixed inset-0 z-[60] bg-black/45 grid place-items-center p-4" data-testid="publish-wizard">
            <div className="bg-white rounded-[16px] shadow-xl w-full max-w-[720px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "#E5E0DA" }}>
                    <div>
                        <h3 className="text-base font-bold text-[#1F1F1F]">Issue / allocate</h3>
                        <div className="text-[11.5px] text-[#9A968F]">{fromTemplate ? `Form: ${source.name}` : "Publish a document or action to a project folder"}</div>
                    </div>
                    <button data-testid="publish-wizard-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X size={16} /></button>
                </div>

                {issuedOpen ? (
                    <SharePanel issue={issuedOpen} onClose={onClose} />
                ) : (
                  <>
                {/* Step rail */}
                <div className="px-5 pt-3 flex items-center gap-2 text-[11px] font-semibold">
                    {STEPS.map((s, i) => (
                        <span key={s} className={`inline-flex items-center gap-1.5 ${s === step ? "text-[#9F4212]" : STEPS.indexOf(step) > i ? "text-[#3F7D3A]" : "text-[#C8C2B8]"}`}>
                            <span className={`w-5 h-5 grid place-items-center rounded-full text-[10px] ${s === step ? "bg-[#FBEFE5]" : STEPS.indexOf(step) > i ? "bg-[#E8F1E7]" : "bg-[#F0EDE8]"}`}>{STEPS.indexOf(step) > i ? <Check size={11} /> : i + 1}</span>
                            <span className="capitalize">{s}</span>
                            {i < STEPS.length - 1 && <ChevronRight size={12} className="text-[#D8D3CA]" />}
                        </span>
                    ))}
                </div>

                <div className="p-5 overflow-y-auto space-y-4 flex-1">
                    {/* TARGET */}
                    {step === "target" && (
                        <div className="space-y-3" data-testid="wizard-step-target">
                            <div className="grid grid-cols-2 gap-3">
                                {isInternal && (
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Client
                                        <select data-testid="wizard-client" value={clientId} onChange={(e) => { setClientId(e.target.value); setProjectId(""); setFolderId(""); }} className={inputCls + " mt-1 font-normal normal-case"}>
                                            <option value="">— Select —</option>
                                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                    </label>
                                )}
                                <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Project
                                    <select data-testid="wizard-project" value={projectId} onChange={(e) => { setProjectId(e.target.value); setFolderId(""); }} disabled={!clientId} className={inputCls + " mt-1 font-normal normal-case disabled:bg-[#F6F4F0]"}>
                                        <option value="">{clientId ? "— Select —" : "Choose a client first"}</option>
                                        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                </label>
                            </div>
                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1.5">Destination folder <span className="text-red-500">*</span></div>
                                <div className="border rounded-[10px] p-2.5 max-h-[260px] overflow-y-auto" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }} data-testid="wizard-folder-picker">
                                    {!projectId ? (
                                        <p className="text-[12px] italic text-[#9A968F] py-3 text-center">Choose a project to see its folders.</p>
                                    ) : folders.length === 0 ? (
                                        <p className="text-[12px] italic text-[#9A968F] py-3 text-center">This project has no folders yet.</p>
                                    ) : (
                                        <FolderTree folders={folders} selectedId={folderId} onSelect={setFolderId} canEdit={false} isInternal hidePseudo />
                                    )}
                                </div>
                                {folderId && <div className="text-[12px] text-[#3F7D3A] mt-1.5 font-semibold flex items-center gap-1"><Check size={13} /> Filing into: {folderName}</div>}
                            </div>
                        </div>
                    )}

                    {/* BEHAVIOUR */}
                    {step === "behaviour" && (
                        <div className="space-y-4" data-testid="wizard-step-behaviour">
                            {(preset?.folderName || folderName) && (
                                <div className="text-[12px] px-3 py-2 rounded-lg flex items-center gap-2" style={{ background: "#FBEFE5", color: "#9F4212" }}>
                                    <FolderOpen size={14} /> Destination: <b>{folderName}</b> {projectLabel ? `· ${projectLabel}` : ""}
                                </div>
                            )}
                            <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block">Title
                                <input data-testid="wizard-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Site induction — Beacon Tower" className={inputCls + " mt-1 font-normal normal-case"} />
                            </label>
                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1.5">What are you issuing?</div>
                                <div className="grid grid-cols-2 gap-2">
                                    {KINDS.map((k) => {
                                        const disabled = fromTemplate && k.key !== "form";
                                        return (
                                            <button key={k.key} data-testid={`wizard-kind-${k.key}`} disabled={disabled}
                                                onClick={() => setKind(k.key)}
                                                className={`text-left p-3 rounded-[10px] border transition-colors ${kind === k.key ? "border-[var(--brand-accent,#C75A1B)] bg-[#FBEFE5]" : "border-[#E5E0DA] bg-white hover:bg-[#F6F4F0]"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
                                                <div className="flex items-center gap-1.5 text-[12.5px] font-bold text-[#1F1F1F]"><k.icon size={14} className="text-[#C68A1C]" /> {k.label}</div>
                                                <div className="text-[11px] text-[#6F6A64] mt-0.5">{k.help}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            {kind === "form" && (
                                <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block">Form template
                                    {fromTemplate ? (
                                        <div className="mt-1 text-[13px] font-semibold text-[#1F1F1F] flex items-center gap-1.5"><FileText size={14} /> {source.name}</div>
                                    ) : (
                                        <select data-testid="wizard-form-template" value={sourceTemplateId} onChange={(e) => { setSourceTemplateId(e.target.value); if (!title) setTitle(formTemplates.find((t) => t.id === e.target.value)?.name || ""); }} className={inputCls + " mt-1 font-normal normal-case"}>
                                            <option value="">— Select a published form —</option>
                                            {formTemplates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.code})</option>)}
                                        </select>
                                    )}
                                </label>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Who completes it?
                                    <select data-testid="wizard-scope" value={scope} onChange={(e) => setScope(e.target.value)} className={inputCls + " mt-1 font-normal normal-case"}>
                                        <option value="any">Any one recipient</option>
                                        <option value="each">Every recipient (each individually)</option>
                                    </select>
                                </label>
                                <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Recipient mode
                                    <select data-testid="wizard-mode" value={mode} onChange={(e) => setMode(e.target.value)} className={inputCls + " mt-1 font-normal normal-case"}>
                                        <option value="named">Named (portal users)</option>
                                        <option value="open">Open link / QR (operatives, no accounts)</option>
                                    </select>
                                </label>
                                {kindDef.type === "action" && (
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Due date
                                        <input data-testid="wizard-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls + " mt-1 font-normal normal-case"} />
                                    </label>
                                )}
                                <label className="flex items-center gap-2 text-[12.5px] text-[#1F1F1F] self-end pb-1">
                                    <input data-testid="wizard-portal-visible" type="checkbox" checked={portalVisible} onChange={(e) => setPortalVisible(e.target.checked)} /> Visible in client portal
                                </label>
                            </div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block">Instructions
                                <textarea data-testid="wizard-instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2} placeholder="Optional note to recipients" className={inputCls + " mt-1 font-normal normal-case h-auto py-2"} />
                            </label>
                        </div>
                    )}

                    {/* RECIPIENTS */}
                    {step === "recipients" && (
                        <div className="space-y-3" data-testid="wizard-step-recipients">
                            {mode === "open" ? (
                                <div className="space-y-2">
                                    <div className="text-[12px] px-3 py-2 rounded-lg flex items-center gap-2" style={{ background: "#EAF1FB", color: "#2A5DB0" }}>
                                        <Link2 size={14} /> A tokenised submission link & QR will be generated when you issue. Operatives sign in by name + signature — no accounts needed.
                                    </div>
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block">Approximate headcount
                                        <input data-testid="wizard-expected" type="number" min={1} value={expectedCount} onChange={(e) => setExpectedCount(e.target.value)} className={inputCls + " mt-1 font-normal normal-case w-40"} />
                                    </label>
                                </div>
                            ) : (
                                <>
                                    <div className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] flex items-center gap-1"><Users size={12} /> Project contacts</div>
                                    <div className="border rounded-[10px] divide-y max-h-[180px] overflow-y-auto" style={{ borderColor: "#E5E0DA" }}>
                                        {people.length === 0 ? <div className="text-[12px] italic text-[#9A968F] p-3">No contacts on this project — add recipients below.</div> :
                                            people.map((p) => (
                                                <label key={p.contact_id} data-testid={`wizard-person-${p.contact_id}`} className="flex items-center gap-2 px-3 py-2 text-[13px] hover:bg-[#F6F4F0] cursor-pointer">
                                                    <input type="checkbox" checked={!!picked[p.contact_id]} onChange={() => togglePerson(p)} />
                                                    <span className="font-semibold text-[#1F1F1F]">{p.name}</span>
                                                    <span className="text-[#9A968F] text-[11px]">{p.email}</span>
                                                    {!p.has_login && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-full bg-[#F0EDE8] text-[#8a8378]">no login</span>}
                                                </label>
                                            ))}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input data-testid="wizard-free-name" value={freeName} onChange={(e) => setFreeName(e.target.value)} placeholder="Add name…" className={inputCls + " flex-1"} />
                                        <input data-testid="wizard-free-email" value={freeEmail} onChange={(e) => setFreeEmail(e.target.value)} placeholder="email (optional)" className={inputCls + " flex-1"} />
                                        <button data-testid="wizard-free-add" onClick={addFree} className="px-3 py-2 rounded-lg text-[12.5px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>Add</button>
                                    </div>
                                    {recipients.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5">
                                            {recipients.map((r, i) => <span key={i} className="text-[11px] px-2 py-1 rounded-full bg-[#FBEFE5] text-[#9F4212] font-semibold">{r.name}</span>)}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* REVIEW */}
                    {step === "review" && (
                        <div className="space-y-2 text-[13px]" data-testid="wizard-step-review">
                            <Row label="Title">{title}</Row>
                            <Row label="Destination">{folderName} · {projectLabel}</Row>
                            <Row label="Type">{kindDef.label}</Row>
                            {kind === "form" && <Row label="Form">{source?.name || formTemplates.find((t) => t.id === sourceTemplateId)?.name}</Row>}
                            <Row label="Completion">{scope === "each" ? "Every recipient" : "Any one recipient"} · {mode === "open" ? "Open link/QR" : "Named"}</Row>
                            {kindDef.type === "action" && dueDate && <Row label="Due">{dueDate}</Row>}
                            <Row label="Recipients">{mode === "open" ? `~${expectedCount} (open link)` : recipients.map((r) => r.name).join(", ")}</Row>
                            <Row label="Portal visible">{portalVisible ? "Yes" : "No"}</Row>
                            {instructions && <Row label="Instructions">{instructions}</Row>}
                        </div>
                    )}
                </div>
                <div className="px-5 py-3 border-t flex items-center justify-between gap-2 bg-[#F5F3F0]" style={{ borderColor: "#E5E0DA" }}>
                    <button onClick={step === STEPS[0] || (preset?.folderId && step === "behaviour") ? onClose : back}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">
                        <ChevronLeft size={14} /> {step === STEPS[0] || (preset?.folderId && step === "behaviour") ? "Cancel" : "Back"}
                    </button>
                    {step !== "review" ? (
                        <button data-testid="wizard-next" onClick={next} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            Next <ChevronRight size={14} />
                        </button>
                    ) : (
                        <div className="flex items-center gap-2">
                            <button data-testid="wizard-save-draft" onClick={() => submit(false)} disabled={busy} className="px-3.5 py-2 text-sm font-semibold rounded-lg border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>Save draft</button>
                            <button data-testid="wizard-issue-now" onClick={() => submit(true)} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Issue now
                            </button>
                        </div>
                    )}
                </div>
                  </>
                )}
            </div>
        </div>
    );
}

function SharePanel({ issue, onClose }) {
    const link = publicLink(issue.submission_token);
    function copy() {
        navigator.clipboard?.writeText(link).then(
            () => toast.success("Link copied"),
            () => toast.error("Could not copy")
        );
    }
    return (
        <div className="p-6 overflow-y-auto" data-testid="publish-wizard-share">
            <div className="text-center mb-4">
                <div className="w-12 h-12 mx-auto rounded-full grid place-items-center mb-2" style={{ background: "#E8F1E7" }}>
                    <Check size={24} className="text-[#3F7D3A]" />
                </div>
                <h3 className="text-base font-bold text-[#1F1F1F]">Issued — ready to share</h3>
                <p className="text-[12.5px] text-[#6F6A64] mt-0.5">{issue.title}</p>
            </div>
            <div className="grid place-items-center mb-4">
                <div className="p-3 bg-white rounded-[12px] border" style={{ borderColor: "#E5E0DA" }} data-testid="share-qr">
                    <QRCodeSVG value={link} size={172} level="M" />
                </div>
                <p className="text-[11px] text-[#9A968F] mt-2 flex items-center gap-1"><QrCode size={12} /> Scan to open the sign-in / submission form</p>
            </div>
            <div className="flex items-center gap-2">
                <input data-testid="share-link" readOnly value={link} className="flex-1 h-9 px-3 rounded-lg border bg-[#FCFBF9] text-[12.5px] text-[#3e4b63]" style={{ borderColor: "#E5E0DA" }} />
                <button data-testid="share-copy" onClick={copy} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                    <Copy size={13} /> Copy
                </button>
            </div>
            <p className="text-[11.5px] text-[#6F6A64] mt-3 flex items-start gap-1.5">
                <Link2 size={13} className="mt-0.5 shrink-0" /> Anyone with this link can submit by name + signature — no account needed. Completions appear against this issue in the project.
            </p>
            <div className="mt-5 flex justify-end">
                <button data-testid="share-done" onClick={onClose} className="px-4 py-2 text-sm font-semibold rounded-lg text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>Done</button>
            </div>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex gap-3">
            <div className="w-28 shrink-0 text-[11px] font-bold uppercase tracking-wider text-[#9A968F]">{label}</div>
            <div className="text-[#1F1F1F] flex-1">{children || "—"}</div>
        </div>
    );
}
